import { useCallback, useEffect, useState } from 'react';

import { alterarCampo, listarCampos } from '../../conteudo/api/conteudo';
import type { CampoConteudo, CamposDaFase, Valor } from '../../conteudo/api/conteudo';
import { CampoDoConteudo, chaveDoCampo, mensagemDaFalha, valorTipado } from './comum';
import type { Aviso } from './comum';

const numeroDo = (local: string, prefixo: string) => Number(local.slice(prefixo.length).split(' ')[0]);

/**
 * Em que ordem as mudanças vão ao servidor: as regras entre campos (`catalogo_menuresolucao.py`)
 * valem a cada gravação, então quem abre espaço vai antes de quem o ocupa — subir o teto antes dos
 * pontos das faixas, baixar as situações exibidas antes de desativar uma situação.
 */
function prioridade(campo: CampoConteudo, novo: Valor): number {
    const anterior = campo.valor;
    if (campo.chave === 'atividade.pontos_maximos') return Number(novo) >= Number(anterior) ? 0 : 6;
    if (campo.chave === 'atividade.quantidade_exibida') return Number(novo) <= Number(anterior) ? 1 : 5;
    if (campo.chave === 'pergunta.ativa') return novo === true ? 2 : 4;
    return 3;
}

/**
 * A aba Menu Resolução de Configurações, no padrão das outras fases: seletor de Pixel (as 5
 * atividades são fixas), a atividade marcada com o tipo e a pontuação, as faixas, e a pergunta de
 * seleção ou as situações de classificação. Tudo fica pendente até "Salvar Alterações".
 */
export function MenuResolucaoConfiguracao({ csrfToken }: { csrfToken: string }) {
    const [dados, setDados] = useState<CamposDaFase | null>(null);
    const [falhouCarregar, setFalhouCarregar] = useState(false);
    const [rascunho, setRascunho] = useState<Record<string, string>>({});
    /** A letra marcada como correta por situação, quando mudou. */
    const [corretas, setCorretas] = useState<Record<string, string>>({});
    const [selecionado, setSelecionado] = useState(1);
    const [salvando, setSalvando] = useState(false);
    const [aviso, setAviso] = useState<Aviso | null>(null);

    const carregar = useCallback(async () => {
        try {
            setDados(await listarCampos('menuresolucao'));
            setFalhouCarregar(false);
        } catch {
            setFalhouCarregar(true);
        }
    }, []);

    useEffect(() => {
        void carregar();
    }, [carregar]);

    function descartar() {
        setRascunho({});
        setCorretas({});
    }

    if (falhouCarregar) {
        return (
            <p className="estado-variaveis mensagem-login">
                Não foi possível carregar o Menu Resolução.{' '}
                <button className="botao-texto" type="button" onClick={() => void carregar()}>Tentar novamente</button>
            </p>
        );
    }
    if (!dados) return <p className="estado-variaveis">Carregando o Menu Resolução…</p>;

    const travado = !dados.editavel;
    const doLocal = (local: string) => dados.campos.filter((campo) => campo.local === local);
    const locais = [...new Set(dados.campos.map((campo) => campo.local))];
    const atividades = locais.filter((local) => /^Atividade \d+$/.test(local))
        .sort((a, b) => numeroDo(a, 'Atividade ') - numeroDo(b, 'Atividade '));
    const marcado = selecionado >= 1 && selecionado <= atividades.length ? selecionado : 1;
    const atividade = atividades[marcado - 1];

    const valorDoCampo = (campo: CampoConteudo) => rascunho[chaveDoCampo(campo)] ?? (campo.valor === null ? '' : String(campo.valor));
    const mudar = (campo: CampoConteudo, valor: string) => setRascunho((atual) => ({ ...atual, [chaveDoCampo(campo)]: valor }));
    const desenharCampo = (campo: CampoConteudo) => (
        <CampoDoConteudo key={chaveDoCampo(campo)} campo={campo} valor={valorDoCampo(campo)} travado={travado} aoMudar={(valor) => mudar(campo, valor)} />
    );
    const campoDe = (local: string, nome: string) => dados.campos.find((campo) => campo.local === local && campo.campo === nome);

    /** A letra correta de uma situação: a escolhida na tela ou a que veio do banco. */
    const letraCorreta = (situacao: string) => corretas[situacao]
        ?? dados.campos.find((campo) => campo.local.startsWith(`${situacao} › Opção `) && campo.campo === 'correta' && campo.valor === true)
            ?.local.split('Opção ').pop() ?? '';

    async function salvar() {
        if (!dados) return;
        setAviso(null);
        const mudancas: { campo: CampoConteudo; valor: Valor }[] = [];
        for (const campo of dados.campos) {
            if (campo.campo === 'correta') continue;
            const digitado = rascunho[chaveDoCampo(campo)];
            const original = campo.valor === null ? '' : String(campo.valor);
            if (digitado === undefined || digitado === original) continue;
            mudancas.push({ campo, valor: valorTipado(campo, digitado) });
        }
        // Marcar a nova correta desmarca a anterior no servidor.
        for (const [situacao, letra] of Object.entries(corretas)) {
            const campo = campoDe(`${situacao} › Opção ${letra}`, 'correta');
            if (campo && campo.valor !== true) mudancas.push({ campo, valor: true });
        }
        mudancas.sort((a, b) => prioridade(a.campo, a.valor) - prioridade(b.campo, b.valor));

        setSalvando(true);
        try {
            for (const { campo, valor } of mudancas) {
                await alterarCampo(campo, valor, campo.campo === 'correta' ? false : campo.valor, csrfToken);
            }
            descartar();
            setAviso({ tipo: 'sucesso', texto: 'Alterações salvas.' });
        } catch (erro) {
            setAviso({ tipo: 'erro', texto: mensagemDaFalha(erro) });
        } finally {
            setSalvando(false);
            await carregar();
        }
    }

    const titulo = atividade ? campoDe(atividade, 'titulo') : undefined;
    const faixas = atividade ? locais.filter((local) => local.startsWith(`${atividade} › Faixa `)) : [];
    const pergunta = atividade ? locais.find((local) => local === `${atividade} › Pergunta`) : undefined;
    const situacoes = atividade
        ? locais.filter((local) => new RegExp(`^${atividade} › Situação \\d+$`).test(local))
            .sort((a, b) => numeroDo(a, `${atividade} › Situação `) - numeroDo(b, `${atividade} › Situação `))
        : [];
    const classificacao = situacoes.length > 0;
    const pontua = atividade ? Boolean(campoDe(atividade, 'pontos_maximos')) : false;
    const resumoDaPontuacao = !pontua ? 'Não pontua' : faixas.length > 0 ? 'Pontua por participação' : 'Pontua por acerto total';
    const opcoesDe = (local: string) => locais.filter((outro) => outro.startsWith(`${local} › Opção `)).sort();
    const camposDaAtividade = atividade
        ? doLocal(atividade).filter((campo) => campo.campo !== 'titulo')
        : [];

    return (
        <div className="config-fase">
            {dados.turmas_com_fase_aberta.length > 0 && (
                <p className="aviso-fase-aberta" role="status">
                    A fase Menu Resolução está aberta na turma {dados.turmas_com_fase_aberta.join(', ')}: só leitura até ela fechar.
                </p>
            )}

            <div className="seletor-enigma-config">
                <label>
                    <span>Pixel:</span>
                    <select aria-label="Pixel" value={String(marcado)} onChange={(evento) => setSelecionado(Number(evento.target.value))}>
                        {atividades.map((_, i) => (
                            <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                        ))}
                    </select>
                </label>
            </div>

            {atividade && (
                <section className="cartao-config" aria-labelledby="mr-atividade">
                    <div className="cabecalho-cartao-config">
                        <h3 id="mr-atividade">{atividade}</h3>
                    </div>
                    <p className="resumo-atividade-config">
                        <span className="selo-config">{classificacao ? 'Classificação' : 'Seleção'}</span>
                        <span className="selo-config">{resumoDaPontuacao}</span>
                    </p>
                    {titulo && desenharCampo(titulo)}
                    {camposDaAtividade.map(desenharCampo)}

                    {faixas.length > 0 && (
                        <section className="cartao-pista" aria-labelledby="mr-faixas">
                            <div className="cabecalho-cartao-config">
                                <h4 id="mr-faixas">Faixas de pontuação</h4>
                            </div>
                            <p className="nota-config">
                                O grupo ganha os pontos da maior faixa que a participação dele alcança. A primeira faixa começa em 0%.
                            </p>
                            {faixas.map((local) => (
                                <div key={local} className="linha-faixa-config" role="group" aria-label={local.split(' › ').pop()}>
                                    <span className="rotulo-faixa-config">{local.split(' › ').pop()}</span>
                                    {doLocal(local).map(desenharCampo)}
                                </div>
                            ))}
                        </section>
                    )}

                    {pergunta && (
                        <section className="cartao-pista" aria-labelledby="mr-pergunta">
                            <div className="cabecalho-cartao-config">
                                <h4 id="mr-pergunta">Pergunta</h4>
                            </div>
                            {doLocal(pergunta).map(desenharCampo)}
                            <div className="alternativas-config">
                                <p className="rotulo-alternativas">Opções <span>(seleção não tem resposta certa)</span></p>
                                {opcoesDe(pergunta).map((local) => {
                                    const texto = campoDe(local, 'texto');
                                    const letra = local.split('Opção ').pop();
                                    return texto && (
                                        <div key={local} className="alternativa-config alternativa-sem-radio">
                                            <span className="letra-alternativa" aria-hidden="true">{letra}</span>
                                            <input
                                                type="text"
                                                aria-label={`Opção ${letra}`}
                                                value={valorDoCampo(texto)}
                                                disabled={travado}
                                                onChange={(evento) => mudar(texto, evento.target.value)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {situacoes.map((situacao) => {
                        const enunciado = campoDe(situacao, 'enunciado');
                        const ativa = campoDe(situacao, 'ativa');
                        const nome = situacao.split(' › ').pop() ?? situacao;
                        const idBase = `mr-${nome.replace(/\s+/g, '-')}`;
                        return (
                            <section key={situacao} className="cartao-pista" aria-labelledby={idBase}>
                                <div className="cabecalho-cartao-config">
                                    <h4 id={idBase}>{nome}</h4>
                                    {ativa && (
                                        <label className="chave-config">
                                            <input
                                                type="checkbox"
                                                checked={valorDoCampo(ativa) === 'true'}
                                                disabled={travado}
                                                onChange={(evento) => mudar(ativa, String(evento.target.checked))}
                                            />
                                            Ativa
                                        </label>
                                    )}
                                </div>
                                {enunciado && desenharCampo(enunciado)}
                                <div className="alternativas-config" role="group" aria-label={`Opções da ${nome}`}>
                                    <p className="rotulo-alternativas">Opções <span>(marque a correta)</span></p>
                                    {opcoesDe(situacao).map((local) => {
                                        const texto = campoDe(local, 'texto');
                                        const letra = local.split('Opção ').pop() ?? '';
                                        return texto && (
                                            <div key={local} className="alternativa-config">
                                                <input
                                                    type="radio"
                                                    name={`${idBase}-correta`}
                                                    aria-label={`Correta ${letra}`}
                                                    checked={letraCorreta(situacao) === letra}
                                                    disabled={travado}
                                                    onChange={() => setCorretas((atual) => ({ ...atual, [situacao]: letra }))}
                                                />
                                                <span className="letra-alternativa" aria-hidden="true">{letra}</span>
                                                <input
                                                    type="text"
                                                    aria-label={`Opção ${letra}`}
                                                    value={valorDoCampo(texto)}
                                                    disabled={travado}
                                                    onChange={(evento) => mudar(texto, evento.target.value)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        );
                    })}
                </section>
            )}

            {aviso && (
                <p className={aviso.tipo === 'erro' ? 'mensagem-login aviso-cadastro' : 'mensagem-salvo'} role={aviso.tipo === 'erro' ? 'alert' : 'status'}>
                    {aviso.texto}
                </p>
            )}

            <div className="rodape-config">
                <button type="button" className="botao-cancelar-config" onClick={descartar} disabled={salvando}>
                    Cancelar
                </button>
                <button type="button" className="botao-salvar-config" onClick={() => void salvar()} disabled={salvando || travado}>
                    {salvando ? 'Salvando…' : 'Salvar Alterações'}
                </button>
            </div>
        </div>
    );
}
